import { EmptyState } from "@heroui-pro/react/empty-state";
import { Button } from "@heroui/react";
import { useCallback } from "react";

import { useAppRouter } from "@/lib/navigation/use-app-router";
import {
  classifyRequestFailure,
  recoveryActionKey,
  type RecoveryAction,
} from "../../lib/errors/request-failure";
import { presentRequestFailure } from "../../lib/errors/present-request-failure";
import { useAppI18n } from "@/i18n/context";
import { NetworkErrorIllustration } from "./network-error-illustration";

type RequestFailureScreenProps = {
  error: unknown;
  fallbackDetail?: string;
  onRetry?: () => void;
};

const resolveHandler = (
  action: RecoveryAction | null,
  handlers: Record<RecoveryAction, (() => void) | undefined>,
) => {
  if (!action) return undefined;
  return handlers[action];
};

/** 阻塞式全页错误：按失败类型展示说明、插图与恢复操作。 */
export function RequestFailureScreen({
  error,
  fallbackDetail,
  onRetry,
}: RequestFailureScreenProps) {
  const { t } = useAppI18n();
  const router = useAppRouter();
  const classification = classifyRequestFailure(error);
  const presented = presentRequestFailure(
    error,
    "page",
    t,
    fallbackDetail ?? t("errors.unavailableDetail"),
  );

  const handleRetry = useCallback(() => {
    if (onRetry) {
      onRetry();
      return;
    }
    window.location.reload();
  }, [onRetry]);

  const handleSignIn = useCallback(() => {
    router.replace("/auth/login");
  }, [router]);

  const handleGoBack = useCallback(() => {
    router.back();
  }, [router]);

  const handleGoHome = useCallback(() => {
    router.replace("/");
  }, [router]);

  const handlers: Record<RecoveryAction, (() => void) | undefined> = {
    goBack: handleGoBack,
    goHome: handleGoHome,
    refresh: handleRetry,
    retry: handleRetry,
    signIn: handleSignIn,
  };

  const title =
    presented.kind === "specific"
      ? t("errors.unavailableTitle")
      : t(presented.titleKey);
  const plan =
    presented.kind === "specific"
      ? {
          primary: classification.primary,
          secondary: classification.secondary,
        }
      : presented.plan;

  const primaryHandler = resolveHandler(plan.primary, handlers);
  const secondaryHandler = resolveHandler(plan.secondary, handlers);

  return (
    <main className="status-screen">
      <EmptyState>
        {classification.kind === "network" ? (
          <EmptyState.Media>
            <NetworkErrorIllustration />
          </EmptyState.Media>
        ) : null}
        <EmptyState.Title>{title}</EmptyState.Title>
        <EmptyState.Description>{presented.description}</EmptyState.Description>
        {classification.requestId ? (
          <p className="request-failure-screen__request-id">
            {t("errors.requestIdLabel")}: {classification.requestId}
          </p>
        ) : null}
        <EmptyState.Content>
          <div className="request-failure-screen__actions">
            {plan.primary && primaryHandler ? (
              <Button onPress={primaryHandler} variant="primary">
                {t(recoveryActionKey(plan.primary))}
              </Button>
            ) : null}
            {plan.secondary && secondaryHandler ? (
              <Button onPress={secondaryHandler} variant="secondary">
                {t(recoveryActionKey(plan.secondary))}
              </Button>
            ) : null}
          </div>
        </EmptyState.Content>
      </EmptyState>
    </main>
  );
}
